// src/lib/api.ts
import { API_BASE_URL } from "@/api/client";
import { translate, translateApiError } from "@/lib/translation";

let currentSessionController = new AbortController();

// Module-level debounce: prevents cascading auth:unauthorized dispatches from
// multiple simultaneous 401 responses (e.g. after hibernation wake-up or
// refetchOnWindowFocus firing many queries at once).
// Reset by abortAllPendingRequests() so a fresh login clears the gate.
let unauthorizedSuppressedUntil = 0;
const UNAUTHORIZED_SUPPRESS_MS = 15_000;

/**
 * Aborts all ongoing authorizedFetch requests AND clears the unauthorized
 * dispatch suppression so a fresh session can respond to 401s normally.
 * Call this on explicit logout, session-expiry handling, and before login.
 */
export const abortAllPendingRequests = () => {
  currentSessionController.abort();
  currentSessionController = new AbortController();
  // Clear the suppression window so a brand-new session isn't silenced.
  unauthorizedSuppressedUntil = 0;
};

interface FastAPIValidationError {
  loc: (string | number)[];
  msg: string;
  type: string;
}

/**
 * Reads a cookie value by name from document.cookie.
 */
export function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

/**
 * Clears the JS-readable `logged_in` marker cookie. Must be deleted with the
 * same Domain it was set with (see COOKIE_DOMAIN on the backend) or the
 * browser treats it as a different cookie and leaves the real one in place.
 */
export function clearLoggedInCookie(): void {
  const isHttps = window.location.protocol === "https:";
  const sameSite = isHttps ? "None" : "Lax";
  const secure = isHttps ? "; Secure" : "";
  const cookieDomain = import.meta.env.VITE_COOKIE_DOMAIN;
  const domain = cookieDomain ? `; Domain=${cookieDomain}` : "";
  document.cookie = `logged_in=; Max-Age=0; Path=/; SameSite=${sameSite}${secure}${domain}`;
}

/**
 * A custom fetch wrapper to handle all API requests.
 * - Sends credentials (httpOnly cookies) automatically via `credentials: 'include'`.
 * - Globally intercepts 401 responses to dispatch 'auth:unauthorized' for redirect.
 * - Standardizes error handling for non-successful responses.
 */
/**
 * Error thrown by authorizedFetch for a non OK response. Carries the HTTP status, so
 * callers branch on it instead of matching message text (which is translated).
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const authorizedFetch = async <T>(
  url: string,
  options: RequestInit = {},
): Promise<T> => {
  const headers = new Headers(options.headers);

  if (!headers.has("Content-Type") && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  // Required by CSRF middleware — browsers cannot set this header in simple
  // cross-origin requests, so its presence proves the request came from JS.
  headers.set("X-Requested-With", "XMLHttpRequest");

  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers,
    credentials: "include",
    signal: options.signal || currentSessionController.signal,
  });

  if (response.status === 403) {
    const forbidden = translate("common:errors.forbidden");
    const errorData = await response
      .json()
      .catch(() => ({ detail: forbidden }));
    window.dispatchEvent(new CustomEvent("auth:email-unverified"));
    throw new Error(
      translateApiError(errorData) ?? (errorData.detail || forbidden),
    );
  }

  if (response.status === 401) {
    console.error(
      "Authentication error: Session expired. Redirecting to login.",
    );
    clearLoggedInCookie();

    // Module-level debounce: only dispatch once per suppress window.
    // Prevents frozen/stale requests (e.g. after hibernation) from triggering
    // repeated session-expiry redirects after the React component's isHandling
    // flag has already reset.
    const now = Date.now();
    if (now > unauthorizedSuppressedUntil) {
      unauthorizedSuppressedUntil = now + UNAUTHORIZED_SUPPRESS_MS;
      const event = new CustomEvent("auth:unauthorized");
      window.dispatchEvent(event);
    }

    throw new Error(translate("common:errors.sessionExpired"));
  }

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ detail: response.statusText }));

    let errorMessage =
      translateApiError(errorData) ??
      (errorData.detail || translate("common:errors.unknownApi"));

    if (errorData.detail && Array.isArray(errorData.detail)) {
      errorMessage = (errorData.detail as FastAPIValidationError[])
        .map((err) => `${err.loc[err.loc.length - 1]}: ${err.msg}`)
        .join("; ");
    }

    throw new ApiError(errorMessage, response.status);
  }

  const contentLength = response.headers.get("content-length");
  if (response.status === 204 || contentLength === "0") {
    return null as T;
  }

  return response.json() as Promise<T>;
};

/**
 * Safely extracts a human-readable message from any thrown value.
 * authorizedFetch already embeds the backend's `detail` field in error.message,
 * so this mainly guards against non-Error throws (network failures, etc.).
 */
export function getApiErrorMessage(error: unknown, fallback?: string): string {
  if (error instanceof Error && error.message) return error.message;
  return fallback ?? translate("common:errors.unexpected");
}
